/**
 * Scoped CSS for Shadow DOM injection.
 * Exported as a single template string — injected via <style> into each Shadow DOM root.
 *
 * Theming uses CSS custom properties so light/dark/auto modes work without JS toggling.
 * No external fonts — inherits host page typography.
 */
export const WIDGET_STYLES = /* css */ `
/* ── Theme custom properties ── */
:host {
  --rss-bg: #ffffff;
  --rss-card-bg: #ffffff;
  --rss-text: #1a1a1a;
  --rss-text-secondary: #6b7280;
  --rss-border: #e5e7eb;
  --rss-skeleton-bg: #e5e7eb;
  --rss-skeleton-shine: #f3f4f6;

  display: block;
  font-family: inherit;
  line-height: inherit;
  color: var(--rss-text);
}

/* Light theme (explicit) */
:host([data-theme="light"]) {
  --rss-bg: #ffffff;
  --rss-card-bg: #ffffff;
  --rss-text: #1a1a1a;
  --rss-text-secondary: #6b7280;
  --rss-border: #e5e7eb;
  --rss-skeleton-bg: #e5e7eb;
  --rss-skeleton-shine: #f3f4f6;
}

/* Dark theme (explicit) */
:host([data-theme="dark"]) {
  --rss-bg: #111111;
  --rss-card-bg: #1e1e1e;
  --rss-text: #e5e5e5;
  --rss-text-secondary: #9ca3af;
  --rss-border: #333333;
  --rss-skeleton-bg: #333333;
  --rss-skeleton-shine: #444444;
}

/* Auto theme — follows prefers-color-scheme */
@media (prefers-color-scheme: dark) {
  :host([data-theme="auto"]) {
    --rss-bg: #111111;
    --rss-card-bg: #1e1e1e;
    --rss-text: #e5e5e5;
    --rss-text-secondary: #9ca3af;
    --rss-border: #333333;
    --rss-skeleton-bg: #333333;
    --rss-skeleton-shine: #444444;
  }
}

/* ── Reset ── */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ── Root container ── */
.rss-widget {
  background: var(--rss-bg);
  font-family: inherit;
  line-height: 1.5;
}

/* ── Grid layout ── */
.rss-grid {
  display: grid;
  grid-template-columns: repeat(var(--rss-columns, 3), 1fr);
  gap: 20px;
}

/* ── Slider layout ── */
.rss-slider {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  gap: 20px;
  scroll-behavior: smooth;
  scrollbar-width: none;
}

.rss-slider::-webkit-scrollbar {
  display: none;
}

.rss-slider > .rss-card {
  flex: 0 0 auto;
  scroll-snap-align: start;
}

/* ── Card ── */
.rss-card {
  background: var(--rss-card-bg);
  border: 1px solid var(--rss-border);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}

.rss-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.rss-card:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.rss-card-image {
  width: 100%;
  height: var(--rss-card-image-height, 200px);
  overflow: hidden;
  background: var(--rss-skeleton-bg);
}

.rss-card-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.rss-card-body {
  padding: 12px;
}

.rss-card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--rss-text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 8px;
}

.rss-card-meta {
  font-size: 0.8125rem;
  color: var(--rss-text-secondary);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Skeleton loading ── */
@keyframes rss-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.rss-skeleton {
  background: var(--rss-card-bg);
  border: 1px solid var(--rss-border);
  border-radius: 8px;
  overflow: hidden;
}

.rss-skeleton .rss-card-image,
.rss-skeleton .rss-skeleton-line {
  background: linear-gradient(
    90deg,
    var(--rss-skeleton-bg) 25%,
    var(--rss-skeleton-shine) 50%,
    var(--rss-skeleton-bg) 75%
  );
  background-size: 200% 100%;
  animation: rss-shimmer 1.5s ease-in-out infinite;
}

.rss-skeleton .rss-card-body {
  padding: 12px;
}

.rss-skeleton-line {
  height: 14px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.rss-skeleton-line:last-child {
  width: 60%;
  margin-bottom: 0;
}

/* ── Modal overlay ── */
.rss-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 0;
}

.rss-modal {
  background: var(--rss-card-bg);
  color: var(--rss-text);
  border-radius: 0;
  width: 100%;
  max-height: 100vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.rss-modal-header {
  padding: 20px 20px 12px;
  border-bottom: 1px solid var(--rss-border);
}

.rss-modal-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: var(--rss-text);
}

.rss-modal-header .rss-card-meta {
  font-size: 0.8125rem;
  color: var(--rss-text-secondary);
}

.rss-modal-content {
  padding: 20px;
  font-size: 1rem;
  line-height: 1.7;
  color: var(--rss-text);
  overflow-wrap: break-word;
}

.rss-modal-content img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.rss-modal-content a {
  color: inherit;
  text-decoration: underline;
}

.rss-modal-content pre {
  overflow-x: auto;
  background: var(--rss-skeleton-bg);
  padding: 12px;
  border-radius: 4px;
  font-size: 0.875rem;
}

.rss-modal-content blockquote {
  border-left: 3px solid var(--rss-border);
  padding-left: 12px;
  margin: 12px 0;
  color: var(--rss-text-secondary);
}

.rss-modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--rss-text-secondary);
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.15s ease;
}

.rss-modal-close:hover {
  background: var(--rss-border);
}

.rss-modal-close:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.rss-share-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--rss-text-secondary);
  background: none;
  border: 1px solid var(--rss-border);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.rss-share-btn:hover {
  background: var(--rss-border);
}

.rss-share-btn:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

/* ── Toast notification ── */
.rss-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(100%);
  background: var(--rss-text);
  color: var(--rss-bg);
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  z-index: 10001;
  pointer-events: none;
}

.rss-toast.rss-toast-visible {
  transform: translateX(-50%) translateY(0);
  opacity: 1;
}

/* ── Error / empty state ── */
.rss-error {
  display: none;
}

/* ── Responsive breakpoints ── */

/* Tablet: ≤768px */
@media (max-width: 768px) {
  .rss-grid {
    grid-template-columns: repeat(var(--rss-columns-tablet, 2), 1fr);
    gap: 16px;
  }

  .rss-slider {
    gap: 16px;
  }

  .rss-modal {
    border-radius: 0;
  }

  .rss-modal-header {
    padding: 16px 16px 10px;
  }

  .rss-modal-content {
    padding: 16px;
  }
}

/* Mobile: ≤480px */
@media (max-width: 480px) {
  .rss-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .rss-slider {
    gap: 12px;
  }

  .rss-modal {
    max-height: 100vh;
    border-radius: 0;
  }

  .rss-modal-header {
    padding: 14px 14px 8px;
  }

  .rss-modal-content {
    padding: 14px;
  }

  .rss-modal-close {
    top: 8px;
    right: 8px;
  }
}
`;
